import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatApiError } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Users, Gavel, Trash2, ShieldCheck, ShieldOff, Play, Pause, ExternalLink, Pencil, Plus, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const fmt = (n, language) => new Intl.NumberFormat(language === "en" ? "en-IE" : "it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function Admin() {
  const { language, t } = useLanguage();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [generating, setGenerating] = useState(false);

  const loadPosts = () => axios.get(`${API}/admin/blog`).then(r => setPosts(r.data)).catch(() => {});
  const genBlog = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/blog/generate`, {});
      toast.success(`${t("Bozza creata: ")}${data.title}`);
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("AI non disponibile")); }
    finally { setGenerating(false); }
  };
  const togglePublish = async (id, published) => {
    try { await axios.patch(`${API}/admin/blog/${id}/publish`, { published }); toast.success(published ? t("Pubblicato") : t("Ripristinato in bozza")); loadPosts(); }
    catch (e) { toast.error(t("Errore")); }
  };
  const deletePost = async (id) => {
    if (!window.confirm(t("Eliminare l'articolo?"))) return;
    try { await axios.delete(`${API}/admin/blog/${id}`); toast.success(t("Eliminato")); loadPosts(); }
    catch (e) { toast.error(t("Errore")); }
  }; // null | {id,name,email,role} | 'new'

  useEffect(() => {
    if (!ready) return;
    if (!user || user === false || user.role !== "admin") { nav("/"); }
  }, [ready, user, nav]);

  const loadStats = () => axios.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
  const loadUsers = () => axios.get(`${API}/admin/users`, { params: q ? { q } : {} }).then(r => setUsers(r.data)).catch(() => {});
  const loadAuctions = () => axios.get(`${API}/admin/auctions`, { params: filterStatus ? { status: filterStatus } : {} }).then(r => setAuctions(r.data)).catch(() => {});

  useEffect(() => { if (user && user.role === "admin") loadStats(); }, [user]);
  useEffect(() => { if (tab === "users" && user?.role === "admin") loadUsers(); }, [tab, q]);
  useEffect(() => { if (tab === "auctions" && user?.role === "admin") loadAuctions(); }, [tab, filterStatus]);
  useEffect(() => { if (tab === "blog" && user?.role === "admin") loadPosts(); }, [tab]);

  const setRole = async (id, role) => {
    try { await axios.patch(`${API}/admin/users/${id}`, { role }); toast.success(`${t("Ruolo aggiornato: ")}${role}`); loadUsers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteUser = async (id) => {
    if (!window.confirm(t("Eliminare definitivamente questo utente?"))) return;
    try { await axios.delete(`${API}/admin/users/${id}`); toast.success(t("Utente eliminato")); loadUsers(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setAuctionStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/auctions/${id}`, { status }); toast.success(`${t("Asta ")}${status}`); loadAuctions(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteAuction = async (id) => {
    if (!window.confirm(t("Eliminare definitivamente questa asta e tutte le sue offerte?"))) return;
    try { await axios.delete(`${API}/admin/auctions/${id}`); toast.success(t("Asta eliminata")); loadAuctions(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user === false || user.role !== "admin") return null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <Seo title="Admin Rarium" description={t("Pannello di amministrazione Rarium")} path="/admin" noindex />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline text-[#8C3A3A]">{t("Pannello amministrativo")}</div>
          <h1 className="font-serif text-5xl tracking-tighter mt-2">Rarium Console.</h1>
        </div>
        <div className="text-sm text-stone-500">
          {t("Loggato come ")}<span className="font-mono">{user.email}</span>
        </div>
      </div>

      <div className="mt-10 border-b border-stone-200 flex gap-8 overflow-x-auto">
        <TabBtn active={tab==="dashboard"} onClick={()=>setTab("dashboard")} testId="admin-tab-dashboard">Dashboard</TabBtn>
        <TabBtn active={tab==="users"} onClick={()=>setTab("users")} testId="admin-tab-users">{t("Utenti")}</TabBtn>
        <TabBtn active={tab==="auctions"} onClick={()=>setTab("auctions")} testId="admin-tab-auctions">{t("Aste")}</TabBtn>
        <TabBtn active={tab==="blog"} onClick={()=>setTab("blog")} testId="admin-tab-blog">{t("Blog")}</TabBtn>
      </div>

      <div className="mt-10">
        {tab === "dashboard" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label={t("Utenti totali")} value={stats.total_users} testId="stat-users" />
            <Kpi label={t("Aste totali")} value={stats.total_auctions} testId="stat-auctions" />
            <Kpi label={t("Aste live")} value={stats.live_auctions} testId="stat-live" accent />
            <Kpi label={t("Aggiudicate")} value={stats.sold_auctions} testId="stat-sold" />
            <Kpi label={t("Offerte piazzate")} value={stats.total_bids} testId="stat-bids" />
            <Kpi label={t("GMV (aggiudicato)")} value={fmt(stats.gmv, language)} testId="stat-gmv" />
            <Kpi label={`${t("Commissioni")} ${stats.platform_fee_percent}%`} value={fmt(stats.platform_fees_earned, language)} testId="stat-fees" accent />
            <Kpi label={t("Fee marketplace")} value={`${stats.platform_fee_percent}%`} testId="stat-fee-percent" />
          </div>
        )}

        {tab === "users" && (
          <div>
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
              <input
                data-testid="admin-user-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("Cerca per email o nome…")}
                className="border border-stone-300 bg-white px-4 py-2 text-sm w-full max-w-sm"
              />
              <div className="flex items-center gap-4">
                <div className="text-sm text-stone-500">{users.length} {t("utenti")}</div>
                <button
                  data-testid="admin-new-user-btn"
                  onClick={() => setEditingUser("new")}
                  className="text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-5 py-3 transition-colors inline-flex items-center gap-2"
                >
                  <Plus size={14} /> {t("Nuovo utente")}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto border border-stone-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-600 uppercase text-xs tracking-widest">
                  <tr>
                    <Th>{t("Utente")}</Th><Th>Email</Th><Th>{t("Ruolo")}</Th><Th>{t("Offerte")}</Th><Th>{t("Lotti")}</Th><Th>{t("Registrato")}</Th><Th>{t("Azioni")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                      <Td>{u.name}</Td>
                      <Td className="font-mono text-xs">{u.email}</Td>
                      <Td>
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${u.role === "admin" ? "bg-[#8C3A3A] text-stone-50" : "bg-stone-200 text-stone-700"}`}>{u.role}</span>
                      </Td>
                      <Td>{u.bid_count}</Td>
                      <Td>{u.listing_count}</Td>
                      <Td className="text-xs text-stone-500">{new Date(u.created_at).toLocaleDateString(language === "en" ? "en-IE" : "it-IT")}</Td>
                      <Td>
                        <div className="flex gap-2">
                          <IconBtn data-testid={`edit-user-${u.id}`} title={t("Modifica")} onClick={() => setEditingUser(u)}><Pencil size={14} /></IconBtn>
                          {u.role === "admin" ? (
                            <IconBtn data-testid={`demote-${u.id}`} title={t("Rimuovi admin")} onClick={() => setRole(u.id, "user")}><ShieldOff size={14} /></IconBtn>
                          ) : (
                            <IconBtn data-testid={`promote-${u.id}`} title={t("Rendi admin")} onClick={() => setRole(u.id, "admin")}><ShieldCheck size={14} /></IconBtn>
                          )}
                          <IconBtn data-testid={`delete-user-${u.id}`} title={t("Elimina")} onClick={() => deleteUser(u.id)} danger><Trash2 size={14} /></IconBtn>
                        </div>
                      </Td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-stone-500">{t("Nessun utente")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "auctions" && (
          <div>
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
              <select
                data-testid="admin-auction-status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-stone-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">{t("Tutti gli stati")}</option>
                <option value="live">Live</option>
                <option value="suspended">{t("Sospese")}</option>
                <option value="sold">{t("Aggiudicate")}</option>
                <option value="ended">{t("Terminate")}</option>
              </select>
              <div className="text-sm text-stone-500">{auctions.length} aste</div>
            </div>
            <div className="overflow-x-auto border border-stone-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-600 uppercase text-xs tracking-widest">
                  <tr>
                    <Th>{t("Lotto")}</Th><Th>{t("Cat.")}</Th><Th>{t("Venditore")}</Th><Th>{t("Offerta att.")}</Th><Th>#</Th><Th>Fee 10%</Th><Th>{t("Stato")}</Th><Th>{t("Termina")}</Th><Th>{t("Azioni")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map(a => (
                    <tr key={a.id} data-testid={`admin-auction-row-${a.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                      <Td className="max-w-[260px] truncate">{a.title}</Td>
                      <Td className="capitalize text-xs">{a.category.replace(/-/g," ")}</Td>
                      <Td className="text-xs">{a.seller_name}</Td>
                      <Td className="font-mono">{fmt(a.current_bid)}</Td>
                      <Td>{a.bid_count}</Td>
                      <Td className="font-mono text-[#8C3A3A]">{fmt(a.current_bid * 0.1)}</Td>
                      <Td>
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${
                          a.status === "live" ? "bg-emerald-100 text-emerald-800" :
                          a.status === "sold" ? "bg-[#8C3A3A] text-stone-50" :
                          a.status === "suspended" ? "bg-amber-100 text-amber-800" :
                          "bg-stone-200 text-stone-700"
                        }`}>{a.status}</span>
                      </Td>
                      <Td className="text-xs text-stone-500">{new Date(a.ends_at).toLocaleDateString(language === "en" ? "en-IE" : "it-IT")}</Td>
                      <Td>
                        <div className="flex gap-2">
                          <Link to={`/auction/${a.id}`} target="_blank" title={t("Apri")}><IconBtn><ExternalLink size={14} /></IconBtn></Link>
                          {a.status === "live" ? (
                            <IconBtn data-testid={`suspend-${a.id}`} title={t("Sospendi")} onClick={() => setAuctionStatus(a.id, "suspended")}><Pause size={14} /></IconBtn>
                          ) : a.status === "suspended" ? (
                            <IconBtn data-testid={`resume-${a.id}`} title={t("Riattiva")} onClick={() => setAuctionStatus(a.id, "live")}><Play size={14} /></IconBtn>
                          ) : null}
                          <IconBtn data-testid={`delete-auction-${a.id}`} title={t("Elimina")} onClick={() => deleteAuction(a.id)} danger><Trash2 size={14} /></IconBtn>
                        </div>
                      </Td>
                    </tr>
                  ))}
                  {auctions.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-12 text-stone-500">{t("Nessuna asta")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === "blog" && (
          <div>
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
              <div className="text-sm text-stone-500">{posts.length} {t("articoli")}</div>
              <button
                data-testid="admin-generate-blog-btn"
                onClick={genBlog}
                disabled={generating}
                className="text-xs uppercase tracking-widest font-semibold bg-[#8C3A3A] hover:bg-[#7F3434] text-stone-50 px-6 py-3 transition-colors disabled:opacity-50"
              >{generating ? t("Genero…") : t("✨ Genera con AI")}</button>
            </div>
            <div className="overflow-x-auto border border-stone-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-600 uppercase text-xs tracking-widest">
                  <tr>
                    <Th>{t("Titolo")}</Th><Th>{t("Categoria")}</Th><Th>{t("Stato")}</Th><Th>{t("Data")}</Th><Th>{t("Azioni")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(p => (
                    <tr key={p.id} data-testid={`admin-blog-row-${p.id}`} className="border-t border-stone-100 hover:bg-stone-50">
                      <Td className="max-w-[380px]">
                        <div className="font-serif text-base leading-tight">{p.title}</div>
                        <div className="text-xs text-stone-500 mt-1 font-mono">/{p.slug}</div>
                      </Td>
                      <Td className="capitalize text-xs">{(p.category||"").replace(/-/g," ")}</Td>
                      <Td>
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${p.published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {p.published ? t("Pubblicato") : t("Bozza")}
                        </span>
                      </Td>
                      <Td className="text-xs text-stone-500">
                        {p.published_at ? new Date(p.published_at).toLocaleDateString(language === "en" ? "en-IE" : "it-IT") : new Date(p.created_at).toLocaleDateString(language === "en" ? "en-IE" : "it-IT")}
                      </Td>
                      <Td>
                        <div className="flex gap-2 flex-wrap">
                          <Link to={`/blog/${p.slug}`} target="_blank"><IconBtn><ExternalLink size={14} /></IconBtn></Link>
                          <IconBtn
                            data-testid={`toggle-publish-${p.id}`}
                            title={p.published ? t("Nascondi") : t("Pubblica")}
                            onClick={() => togglePublish(p.id, !p.published)}
                          >{p.published ? <Pause size={14} /> : <Play size={14} />}</IconBtn>
                          <IconBtn data-testid={`delete-blog-${p.id}`} title="Elimina" onClick={() => deletePost(p.id)} danger><Trash2 size={14} /></IconBtn>
                        </div>
                      </Td>
                    </tr>
                  ))}
                  {posts.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-12 text-stone-500">{t("Nessun articolo. Clicca \"Genera con AI\" per crearne uno.")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-xs text-stone-500 leading-relaxed max-w-3xl">
              <strong>{t("Come funziona:")}</strong> {t("Il generatore AI usa Claude Sonnet 5 per scrivere un articolo di 800-1200 parole sulla categoria con meno articoli pubblicati (rotazione automatica). L'articolo viene salvato in bozza — clicca sul pulsante ▶ per pubblicarlo. In background, uno scheduler settimanale pubblica automaticamente un articolo se sono passati più di 6 giorni dall'ultima pubblicazione.")}
            </div>
          </div>
        )}
      </div>

      {editingUser && (
        <UserModal
          initial={editingUser === "new" ? null : editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); loadUsers(); loadStats(); }}
        />
      )}
    </div>
  );
}

function UserModal({ initial, onClose, onSaved }) {
  const { t } = useLanguage();
  const isNew = !initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    role: initial?.role || "user",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) {
        if (!form.password || form.password.length < 6) {
          toast.error(t("Password minimo 6 caratteri")); setBusy(false); return;
        }
        await axios.post(`${API}/admin/users`, form);
        toast.success(t("Utente creato"));
      } else {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) {
          if (form.password.length < 6) { toast.error(t("Password minimo 6 caratteri")); setBusy(false); return; }
          payload.password = form.password;
        }
        await axios.patch(`${API}/admin/users/${initial.id}`, payload);
        toast.success(t("Utente aggiornato"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Errore"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="user-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-stone-50 max-w-md w-full p-8 shadow-2xl relative"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="user-modal-close"
          className="absolute top-4 right-4 text-stone-500 hover:text-stone-900"
        ><X size={18} /></button>
        <div className="overline text-[#8C3A3A]">{isNew ? t("Nuovo utente") : t("Modifica utente")}</div>
        <h2 className="font-serif text-3xl tracking-tighter mt-2">
          {isNew ? t("Crea account") : form.name}
        </h2>

        <div className="mt-6 flex flex-col gap-5">
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">{t("Nome")}</span>
            <input
              data-testid="user-modal-name"
              value={form.name}
              onChange={upd("name")}
              required
              className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none"
            />
          </label>
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">Email</span>
            <input
              data-testid="user-modal-email"
              type="email"
              value={form.email}
              onChange={upd("email")}
              required
              className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none"
            />
          </label>
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">
              {t("Password")} {isNew ? t("(min. 6)") : t(" (lascia vuoto per non cambiare)")}
            </span>
            <input
              data-testid="user-modal-password"
              type="password"
              value={form.password}
              onChange={upd("password")}
              placeholder={isNew ? "" : "••••••••"}
              className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none"
            />
          </label>
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">{t("Ruolo")}</span>
            <select
              data-testid="user-modal-role"
              value={form.role}
              onChange={upd("role")}
              className="border border-stone-300 bg-white p-3"
            >
              <option value="user">{t("Utente")}</option>
              <option value="admin">Admin</option>
            </select>
          </label>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-widest font-semibold border border-stone-900 px-6 py-3 hover:bg-stone-100 transition-colors"
          >{t("Annulla")}</button>
          <button
            type="submit"
            data-testid="user-modal-submit"
            disabled={busy}
            className="flex-1 text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-6 py-3 transition-colors disabled:opacity-50"
          >{busy ? "…" : isNew ? t("Crea utente") : t("Salva modifiche")}</button>
        </div>
      </form>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`pb-3 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? "border-[#8C3A3A] text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}>{children}</button>
);
const Kpi = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className={`border p-6 ${accent ? "border-[#8C3A3A] bg-[#8C3A3A]/5" : "border-stone-200 bg-white"}`}>
    <div className="overline text-stone-500">{label}</div>
    <div className={`font-serif text-3xl mt-2 tracking-tighter ${accent ? "text-[#8C3A3A]" : ""}`}>{value}</div>
  </div>
);
const Th = ({ children }) => <th className="text-left px-4 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const IconBtn = ({ children, danger, ...rest }) => (
  <button {...rest} className={`p-2 border border-stone-200 hover:border-stone-900 transition-colors ${danger ? "hover:bg-[#8C3A3A] hover:text-stone-50 hover:border-[#8C3A3A]" : "hover:bg-stone-100"}`}>{children}</button>
);
